<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { useStore } from "../../../../store/index";
import axios from "axios";
import { suggestedDomainsList, Domains } from "../../../../utils/constants";
import { ref, Ref, watchEffect } from "vue";
import { addRetargetingScripts } from "../../../../utils/retargeting";
import { pushEmail } from "../../../../utils/pu";
const emit = defineEmits(["nextStep"]);
const { t } = useI18n();
const store = useStore();

const email = ref("");
const emailError = ref();
const isLoading = ref(false);
const lpDomain = ref(window.location.host + window.location.pathname);

const showSkipButton = ref(false);
const trackingDomain = store.trackingDomain;

const re =
	/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const country = ref(
	store.urlParameters?.lang?.slice(0, 2) ?? "hu",
) as unknown as Ref<Domains>;
const showSuggestion = ref(false);

const suggestedResults = () =>
	suggestedDomainsList[country.value].map((domain: string) => {
		return email.value.split("@")[0] + domain;
	});

const watcher = watchEffect(() => {
	if (email.value.includes("@")) {
		suggestedResults();
		showSuggestion.value = true;
	}
	if (re.test(email.value)) {
		showSuggestion.value = false;
	}
});

function selectDomain(address: string) {
	email.value = address;
	showSuggestion.value = false;
}

function skipButton() {
	if (store.isAboveAge === true) {
		window.location.href = `https://${trackingDomain}/click/2`;
	} else {
		window.location.href = `https://${trackingDomain}/click/1`;
	}
}

function startCounter() {
	setTimeout(function () {
		showSkipButton.value = true;
	}, 4000);
}

function forwarder(click: number) {
	if (store.urlParameters?.forward === "nva") {
		window.location.href = `https://${trackingDomain}/click/${click}?sub5=${email.value}`;
	}
	if (store.urlParameters?.forward === "to") {
		const emailBase64 = ref(btoa(`{"email": "${email.value}"}`));
		window.location.href = `https://${trackingDomain}/click/${click}?_fData=${emailBase64.value}`;
	}
	if (store.urlParameters?.forward === "tm") {
		window.location.href = `https://${trackingDomain}/click/${click}?email=${email.value}`;
	}
	if (store.urlParameters?.forward === "moar") {
		window.location.href = `https://${trackingDomain}/click/${click}?email=${email.value}`;
	}
	if (store.urlParameters?.forward === "nex") {
		window.location.href = `https://${trackingDomain}/click/${click}?email=${email.value}`;
	} else {
		window.location.href = `https://${trackingDomain}/click/${click}`;
	}
}

async function validateEmail() {
	isLoading.value = true;
	if (email.value === undefined) {
		emailError.value = t("age.step2.errorNoEmail");
		isLoading.value = false;
		startCounter();
	} else if (!re.test(email.value)) {
		emailError.value = t("age.step2.errorInvalidEmail");
		isLoading.value = false;
		startCounter();
	} else {
		emailError.value = undefined;

		await pushEmail(
			email.value,
			store.urlParameters,
			window.location.host + window.location.pathname,
		);

		if (store.isAboveAge === true) {
			forwarder(2);
		} else {
			forwarder(1);
		}
	}
}

function checkInputFocusCondition() {
	addRetargetingScripts();
}
</script>

<template>
	<div
		class="flex flex-col items-center w-full max-w-2xl mx-auto space-y-5 text-center mt-7"
	>
		<h1 class="text-[#677277] tracking-[0.2rem] uppercase font-semibold">
			{{ t("age.step2.text1") }}
		</h1>

		<div>
			<input
				type="email"
				name=""
				id=""
				class="rounded-sm"
				:placeholder="t('age.step2.inputPlaceholder')"
				autocomplete="off"
				v-model="email"
				@focus="checkInputFocusCondition()"
			/>
			<div class="relative z-30 max-w-xl">
				<ul
					v-if="showSuggestion && store.urlParameters?.ehelp"
					class="absolute w-full overflow-y-scroll bg-white border border-gray-300 rounded h-36"
				>
					<li
						v-for="domain in suggestedResults()"
						:key="domain"
						class="p-1 cursor-pointer hover:bg-gray-400"
						@click="selectDomain(domain)"
					>
						{{ domain }}
					</li>
				</ul>
			</div>
			<div class="h-6 mt-2">
				<h1
					v-if="emailError"
					class="font-semibold text-center text-red-600"
				>
					{{ emailError }}
				</h1>
			</div>
		</div>

		<div
			class="flex flex-col items-center justify-center w-full max-w-sm space-y-3 sm:flex-row sm:space-x-4 sm:space-y-0"
		>
			<button
				:disabled="isLoading"
				class="border border-[#cbcfd2] py-2 px-6 rounded-md text-sm hover:border-red-400 hover:text-red-400 duration-200 uppercase font-bold w-1/2"
				@click="validateEmail()"
			>
				{{ t("age.step2.submitButton") }}
			</button>
			<button
				:disabled="isLoading"
				v-if="showSkipButton"
				class="border border-[#cbcfd2] py-2 px-6 rounded-md text-sm hover:border-red-400 hover:text-red-400 duration-200 uppercase font-bold w-1/2 fadeInUp"
				@click="skipButton()"
			>
				{{ t("age.step2.skipButton") }}
			</button>
		</div>
	</div>
</template>
